import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO, startOfToday, subMonths, addMonths } from 'date-fns'
import { de } from 'date-fns/locale'
import {
    CalendarIcon,
    ClockIcon,
    UsersIcon,
    ArrowLeftIcon,
    PhoneIcon,
    MapPinIcon,
    BabyIcon,
    EuroIcon,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
    SessionDetailDialog,
    type SessionDetailData,
} from '@/components/session-detail-dialog'
import { HonorariumTiersConfig } from '@/components/admin/honorarium-tiers-config'
import { HonorariumGenerationSection } from '@/components/admin/honorarium-generation-section'
import { HonorariumHistoryList } from '@/components/admin/honorarium-history-list'
import { getTrainer, getTrainerCourses, getTrainerPublicSessions, getCurrentUserRole } from '@/lib/api'
import { queryKeys } from '@/lib/query-keys'
import type { PublicSession, TrainerCourse } from '@/types/domain'

function sessionToDetailData(session: PublicSession): SessionDetailData {
    return {
        courseName: session.course_name,
        courseDescription: session.description,
        startsAt: session.starts_at,
        endsAt: session.ends_at,
        trainerName: session.trainer_name,
        trainerId: session.trainer_id,
        level: session.level,
        availableSpots: session.available_spots,
        maxParticipants: session.max_participants,
        status: null, // Public view doesn't show user booking status
        registrationUntil: session.registration_until,
        cancellationUntil: session.cancellation_until,
    }
}

function SessionCard({ session, onClick }: { session: PublicSession; onClick: () => void }) {
    return (
        <Card
            className="border-border/80 transition hover:shadow-md cursor-pointer"
            onClick={onClick}
        >
            <CardContent className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{session.course_name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {format(parseISO(session.starts_at), "EEEE, dd. MMMM yyyy", { locale: de })}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                        <ClockIcon className="h-3.5 w-3.5" />
                        {format(parseISO(session.starts_at), 'HH:mm')} – {format(parseISO(session.ends_at), 'HH:mm')}
                    </p>
                </div>
                <div className="flex items-center gap-4 mt-2 md:mt-0">
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <UsersIcon className="h-3.5 w-3.5" />
                        {session.available_spots} / {session.max_participants} frei
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

function CourseCard({ course }: { course: TrainerCourse }) {
    return (
        <Card className="h-full">
            <CardHeader>
                <div className="flex justify-between items-start gap-2">
                    <CardTitle className="text-lg">{course.name}</CardTitle>
                    {course.level && <Badge variant="outline">{course.level}</Badge>}
                </div>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {course.description || 'Keine Beschreibung verfügbar.'}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: course.color }} />
                    <span>{course.session_count} Mal gehalten</span>
                </div>
            </CardContent>
        </Card>
    )
}

export function TrainerDetailPage() {
    const { trainerId } = useParams<{ trainerId: string }>()
    const [selectedSession, setSelectedSession] = useState<PublicSession | null>(null)
    const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming')

    const userQuery = useQuery({
        queryKey: ['current-user-role'],
        queryFn: getCurrentUserRole,
    })

    const trainerQuery = useQuery({
        queryKey: queryKeys.trainerDetails(trainerId!),
        queryFn: () => getTrainer(trainerId!),
        enabled: !!trainerId,
    })

    const coursesQuery = useQuery({
        queryKey: queryKeys.trainerCourses(trainerId!),
        queryFn: () => getTrainerCourses(trainerId!),
        enabled: !!trainerId,
    })

    const today = startOfToday()
    const threeMonthsAgo = subMonths(today, 3)
    const threeMonthsFuture = addMonths(today, 3)

    const sessionsQuery = useQuery({
        queryKey: queryKeys.trainerSessions(trainerId!, threeMonthsAgo.toISOString(), threeMonthsFuture.toISOString()),
        queryFn: () => getTrainerPublicSessions(trainerId!, threeMonthsAgo, threeMonthsFuture),
        enabled: !!trainerId,
    })

    const { upcoming, past } = useMemo(() => {
        const list = sessionsQuery.data ?? []
        const up: PublicSession[] = []
        const pa: PublicSession[] = []

        list.forEach((s) => {
            if (parseISO(s.starts_at) >= today) {
                up.push(s)
            } else {
                pa.push(s)
            }
        })

        return {
            upcoming: up, // Already sorted asc by RPC
            past: pa.sort((a, b) => b.starts_at.localeCompare(a.starts_at)), // Sort desc
        }
    }, [sessionsQuery.data, today])

    if (!trainerId) return <div>Trainer ID fehlt.</div>

    if (trainerQuery.isLoading) {
        return (
            <div className="space-y-6 container py-10">
                <Skeleton className="h-10 w-1/3" />
                <Skeleton className="h-64 w-full" />
            </div>
        )
    }

    if (trainerQuery.isError || !trainerQuery.data) {
        return (
            <div className="container py-10 text-center">
                <h2 className="text-xl font-semibold text-destructive">Trainer nicht gefunden</h2>
                <Button asChild variant="ghost" className="mt-4">
                    <Link to="/admin/users/staff"><ArrowLeftIcon className="mr-2 h-4 w-4" /> Zurück zur Mitarbeiterverwaltung</Link>
                </Button>
            </div>
        )
    }

    const trainer = trainerQuery.data
    const isAdmin = userQuery.data?.role === 'admin'
    const isOwner = userQuery.data?.id === trainerId

    return (
        <div className="container py-10 space-y-8">
            <div>
                {isAdmin && (
                    <Button asChild variant="ghost" size="sm" className="mb-4 text-muted-foreground">
                        <Link to="/admin/users/staff"><ArrowLeftIcon className="mr-2 h-4 w-4" /> Zurück zur Übersicht</Link>
                    </Button>
                )}
                <div className="flex flex-col md:flex-row gap-6 items-start">
                    {trainer.avatar_url && (
                        <img
                            src={trainer.avatar_url}
                            alt={trainer.full_name ?? ''}
                            className="w-24 h-24 rounded-full object-cover border-2 border-primary/20"
                        />
                    )}
                    <div className="space-y-1">
                        <h1 className="text-3xl font-bold">{trainer.full_name}</h1>
                        <p className="text-muted-foreground text-lg italic">Trainer bei Motionlab</p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm">
                            {trainer.phone_number && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <PhoneIcon className="h-4 w-4" />
                                    <span>{trainer.phone_number}</span>
                                </div>
                            )}
                            {(trainer.street || trainer.house_number || trainer.postal_code || trainer.city) && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <MapPinIcon className="h-4 w-4" />
                                    <span>
                                        {trainer.street} {trainer.house_number}
                                        {((trainer.street || trainer.house_number) && (trainer.postal_code || trainer.city)) ? ', ' : ''}
                                        {trainer.postal_code} {trainer.city}
                                    </span>
                                </div>
                            )}
                            {trainer.birth_date && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <BabyIcon className="h-4 w-4" />
                                    <span>Geboren am {format(parseISO(trainer.birth_date), 'dd.MM.yyyy')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="courses" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="courses">Kurse ({coursesQuery.data?.length ?? 0})</TabsTrigger>
                    <TabsTrigger value="schedule">Terminplan</TabsTrigger>
                    {(isAdmin || isOwner) && (
                        <TabsTrigger value="honorarium" className="gap-2">
                            <EuroIcon className="h-4 w-4" /> Honorar
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="courses" className="space-y-6">
                    <div>
                        <h2 className="text-xl font-semibold mb-4">Unterrichtete Kurse</h2>
                        {coursesQuery.isLoading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <Skeleton className="h-40" />
                                <Skeleton className="h-40" />
                            </div>
                        ) : coursesQuery.data?.length === 0 ? (
                            <p className="text-muted-foreground">Keine Kurse gefunden.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {coursesQuery.data?.map((course) => (
                                    <CourseCard key={course.id} course={course} />
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="schedule" className="space-y-6">
                    <Tabs value={tab} onValueChange={(v) => setTab(v as 'upcoming' | 'past')}>
                        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
                            <TabsTrigger value="upcoming">Kommende ({upcoming.length})</TabsTrigger>
                            <TabsTrigger value="past">Vergangene ({past.length})</TabsTrigger>
                        </TabsList>

                        <TabsContent value="upcoming" className="mt-4 space-y-4">
                            {sessionsQuery.isLoading ? (
                                <Skeleton className="h-24 w-full" />
                            ) : upcoming.length === 0 ? (
                                <p className="text-muted-foreground py-8 text-center">Keine kommenden Termine geplant.</p>
                            ) : (
                                upcoming.map((session) => (
                                    <SessionCard
                                        key={session.session_id}
                                        session={session}
                                        onClick={() => setSelectedSession(session)}
                                    />
                                ))
                            )}
                        </TabsContent>

                        <TabsContent value="past" className="mt-4 space-y-4">
                            {sessionsQuery.isLoading ? (
                                <Skeleton className="h-24 w-full" />
                            ) : past.length === 0 ? (
                                <p className="text-muted-foreground py-8 text-center">Keine vergangenen Termine in den letzten 3 Monaten.</p>
                            ) : (
                                past.map((session) => (
                                    <SessionCard
                                        key={session.session_id}
                                        session={session}
                                        onClick={() => setSelectedSession(session)}
                                    />
                                ))
                            )}
                        </TabsContent>
                    </Tabs>
                </TabsContent>

                {(isAdmin || isOwner) && (
                    <TabsContent value="honorarium" className="space-y-8 animate-in fade-in-50 duration-300">
                        {isAdmin && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2">
                                    <HonorariumGenerationSection
                                        trainerId={trainerId}
                                    />
                                </div>
                                <div className="lg:col-span-1">
                                    <HonorariumTiersConfig trainerId={trainerId} />
                                </div>
                            </div>
                        )}
                        <HonorariumHistoryList trainerId={trainerId} isAdmin={isAdmin} />
                    </TabsContent>
                )}
            </Tabs>

            <SessionDetailDialog
                open={selectedSession !== null}
                onOpenChange={(open) => {
                    if (!open) setSelectedSession(null)
                }}
                session={selectedSession ? sessionToDetailData(selectedSession) : null}
                title="Kursdetails"
                description={`Informationen zum Kurs von ${trainer.full_name}`}
                isAdmin={true}
            />
        </div>
    )
}
